/**
 * User Management React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserService } from '../api/services/userService';
import type { InviteUserRequest } from '../api/types';

// Query keys
export const userKeys = {
  all: ['users'] as const,
  list: (params?: any) => [...userKeys.all, 'list', params] as const,
  detail: (id: string) => [...userKeys.all, id] as const,
  sessions: (id: string) => [...userKeys.all, id, 'sessions'] as const,
  roles: (id: string) => [...userKeys.all, id, 'roles'] as const,
  effectivePermissions: (id: string) => [...userKeys.all, id, 'effective-permissions'] as const,

  invites: {
    all: ['invites'] as const,
    list: (params?: any) => [...userKeys.invites.all, 'list', params] as const,
    detail: (token: string) => [...userKeys.invites.all, 'token', token] as const,
  },

  audit: {
    all: ['audit'] as const,
    list: (params?: any) => [...userKeys.audit.all, 'list', params] as const,
    actions: () => [...userKeys.audit.all, 'actions'] as const,
    stats: (params?: any) => [...userKeys.audit.all, 'stats', params] as const,
  },
};

// Users
export function useUsers(params?: {
  page?: number;
  size?: number;
  organization_id?: string;
  status?: string;
  role?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: userKeys.list(params),
    queryFn: () => UserService.listUsers(params),
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => UserService.getUser(id),
    enabled: !!id,
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: InviteUserRequest) => UserService.inviteUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: userKeys.invites.all });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<{ name: string; phone: string; role: string; status: string }> }) =>
      UserService.updateUser(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => UserService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

export function usePermanentDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => UserService.permanentDeleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

export function usePromoteToGlobalAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => UserService.promoteToGlobalAdmin(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
    },
  });
}

export function useDemoteFromGlobalAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => UserService.demoteFromGlobalAdmin(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
    },
  });
}

export function useResendInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => UserService.resendInvite(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.invites.all });
    },
  });
}

// Sessions
export function useUserSessions(userId: string) {
  return useQuery({
    queryKey: userKeys.sessions(userId),
    queryFn: () => UserService.getUserSessions(userId),
    enabled: !!userId,
  });
}

export function useTerminateSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, sessionId }: { userId: string; sessionId: string }) =>
      UserService.terminateSession(userId, sessionId),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: userKeys.sessions(userId) });
    },
  });
}

export function useTerminateAllSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => UserService.terminateAllSessions(userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: userKeys.sessions(userId) });
    },
  });
}

// Invites
export function useInvites(params?: {
  page?: number;
  size?: number;
  organization_id?: string;
}) {
  return useQuery({
    queryKey: userKeys.invites.list(params),
    queryFn: () => UserService.listInvites(params),
  });
}

export function useRevokeInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => UserService.revokeInvite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.invites.all });
    },
  });
}

// Public invite hooks (no auth required)
export function useInviteDetails(token: string) {
  return useQuery({
    queryKey: userKeys.invites.detail(token),
    queryFn: () => UserService.getInviteDetails(token),
    enabled: !!token,
    retry: false,
  });
}

export function useAcceptInvite() {
  return useMutation({
    mutationFn: ({ token, data }: { token: string; data: { password: string; name?: string } }) =>
      UserService.acceptInvite(token, data),
  });
}

// Audit
export function useAuditLog(params?: {
  page?: number;
  size?: number;
  organization_id?: string;
  user_id?: string;
  action?: string;
  resource_type?: string;
  resource_id?: string;
  from?: string;
  to?: string;
}) {
  return useQuery({
    queryKey: userKeys.audit.list(params),
    queryFn: () => UserService.listAuditEntries(params),
  });
}

export function useAuditActions() {
  return useQuery({
    queryKey: userKeys.audit.actions(),
    queryFn: () => UserService.getAuditActions(),
    staleTime: 300000, // 5 minutes
  });
}

export function useAuditStats(params?: {
  organization_id?: string;
  days?: number;
}) {
  return useQuery({
    queryKey: userKeys.audit.stats(params),
    queryFn: () => UserService.getAuditStats(params),
  });
}

export function useExportAuditLog() {
  return useMutation({
    mutationFn: (params: {
      format?: 'json' | 'csv';
      organization_id?: string;
      user_id?: string;
      action?: string;
      from?: string;
      to?: string;
    }) => UserService.exportAuditLog(params),
    onSuccess: (blob, { format = 'json' }) => {
      // Download the file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
  });
}

// User Roles
export function useUserRoles(userId: string) {
  return useQuery({
    queryKey: userKeys.roles(userId),
    queryFn: () => UserService.getUserRoles(userId),
    enabled: !!userId,
  });
}

export function useAssignUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: {
      userId: string;
      data: {
        role_id: string;
        organization_id?: string;
        resource_type?: string;
        resource_id?: string;
        expires_at?: string;
      };
    }) => UserService.assignUserRole(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: userKeys.roles(userId) });
      queryClient.invalidateQueries({ queryKey: userKeys.effectivePermissions(userId) });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(userId) });
    },
  });
}

export function useRemoveUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, assignmentId }: { userId: string; assignmentId: string }) =>
      UserService.removeUserRole(userId, assignmentId),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: userKeys.roles(userId) });
      queryClient.invalidateQueries({ queryKey: userKeys.effectivePermissions(userId) });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(userId) });
    },
  });
}

export function useUserEffectivePermissions(userId: string) {
  return useQuery({
    queryKey: userKeys.effectivePermissions(userId),
    queryFn: () => UserService.getEffectivePermissions(userId),
    enabled: !!userId,
  });
}
